// Dependencies
import { useSnackbar } from "notistack";
import { FC, createContext, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

// Components
import { If } from "jsx-control-statements";
import { SearchBar } from "~/components/Common";
import LayoutDelete from "~/components/Layout/WalletDelete";
import TransactionList from "~/components/Transactions/TransactionList";
import TransactionInfo from "~/components/Transactions/TransactionInfo";
import TransactionEdit from "~/components/Transactions/TransactionEdit";

// apis
import {
  editTransaction,
  deleteTransaction,
  getTransactionInfo,
} from "~/services/transaction";

// Types
import { QUERY_KEY } from "~/enums";
import { TTransactionData, TTransactionEditData, TQueryError } from "~/types";

// Utils
import { getChangedValues } from "~/utils";

// Styles
import {
  StyledLayoutContent,
  StyledLayoutWrapper,
} from "../../components/Layout";
import { StyledModal } from "~/components/Layout/LayoutInfo";

export const TransactionContext = createContext<{
  handleShowDetail: Function;
}>({ handleShowDetail: () => {} });

interface TransactionsProps {}

const Transactions: FC<TransactionsProps> = () => {
  // States
  const [transactionId, setTransactionId] = useState<string>("");
  const [walletCurrency, setWalletCurrency] = useState<string>("");
  const [walletId, setWalletId] = useState<string>("");
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Queries
  const transactionQuery = useQuery({
    queryKey: [QUERY_KEY.TRANSACTION.TRANSACTION_INFO, transactionId],
    queryFn: () => getTransactionInfo(transactionId),
    enabled: !!transactionId,
    onError(error: TQueryError) {},
  });

  const transactionData: TTransactionData | undefined = transactionQuery.data;

  // Mutations
  const editTransactionMutation = useMutation({
    mutationFn: editTransaction,
    onSuccess() {
      enqueueSnackbar("Edit Transaction success!", { variant: "success" });
      queryClient.invalidateQueries(QUERY_KEY.TRANSACTION.TRANSACTION_INFO);
      queryClient.invalidateQueries(QUERY_KEY.TRANSACTION.TRANSACTION_LIST);

      /** close edit when saving successful */
      handleCancelEdit();
    },
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const deleteTransactionMutation = useMutation({
    mutationFn: deleteTransaction,
    onSuccess() {
      enqueueSnackbar("Delete Transaction success!", { variant: "success" });
      queryClient.invalidateQueries(QUERY_KEY.TRANSACTION.TRANSACTION_LIST);
      setShowDelete(false);
      setShowDetail(false);
      setTransactionId("");
    },
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  // method
  /** Show Transaction Detail */
  const handleShowDetail = (
    transactionId: string,
    walletCurrency: string,
    walletId: string
  ) => {
    setShowEdit(false);
    setShowDetail(true);
    setTransactionId(transactionId);
    setWalletId(walletId);
    setWalletCurrency(walletCurrency);
  };
  /************************************ */

  /** Edit Wallet */
  const handleShowEdit = () => {
    setShowEdit(true);
    setShowDetail(false);
  };

  const handleSaveEdit = (data: TTransactionEditData) => {
    if (transactionData?.id) {
      const editData = getChangedValues(transactionData, data);
      editTransactionMutation.mutate({
        ...editData,
        transactionId: transactionData.id,
      });
    }
  };

  const handleCancelEdit = () => {
    setShowEdit(false);
    setShowDetail(true);
  };
  /************************************* */

  /** Delete Wallet */
  const handleShowDelete = () => {
    setShowDelete(true);
  };

  const handleCloseDelete = () => {
    setShowDelete(false);
  };

  const handleDelete = () => {
    deleteTransactionMutation.mutate(transactionId);
  };
  /************************************ */

  const contextValue = { handleShowDetail };

  return (
    <StyledLayoutWrapper>
      <SearchBar />
      <StyledLayoutContent>
        <TransactionContext.Provider value={contextValue}>
          <TransactionList />
        </TransactionContext.Provider>
        <If condition={showDetail}>
          <TransactionInfo
            data={transactionData}
            status={{ ...transactionQuery }}
            error={transactionQuery?.error?.message}
            currency={walletCurrency}
            onEdit={handleShowEdit}
            onDelete={handleShowDelete}
          />
        </If>
        <If condition={showEdit}>
          <TransactionEdit
            initialData={transactionData}
            walletId={walletId}
            onSubmit={handleSaveEdit}
            onCancel={handleCancelEdit}
          />
        </If>
      </StyledLayoutContent>
      <StyledModal open={showDelete}>
        <div>
          <LayoutDelete
            title={"Are you sure to delete this transaction?"}
            name={`${transactionData?.categoryName}${
              transactionData?.note ? `- ${transactionData?.note}` : ""
            }`}
            onCancel={handleCloseDelete}
            onConfirm={handleDelete}
          />
        </div>
      </StyledModal>
    </StyledLayoutWrapper>
  );
};

export default Transactions;
